import React from "react";

interface Props {
  /** 0–100, or null when the trip's checklist was never built. */
  percent: number | null;
  /** Outer diameter in px. */
  size?: number;
  /** Shown under the number, inside the ring, when there is room. */
  caption?: string;
}

/**
 * Trip readiness as a ring (UX_CONCEPT.md §5.1, §5.3). Tangerine while steps
 * are open, green at 100%. With no checklist yet it is a dashed ring and a
 * dash — "not looked at" is not the same as 0%.
 */
export const ReadinessRing: React.FC<Props> = ({
  percent,
  size = 84,
  caption,
}) => {
  const stroke = Math.max(6, Math.round(size / 9));
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const ready = percent === 100;
  const label =
    percent === null ? "Readiness not checked yet" : `${percent}% trip-ready`;

  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={label}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          className="stroke-muted"
          strokeWidth={stroke}
          strokeDasharray={percent === null ? "4 6" : undefined}
        />
        {percent !== null && percent > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            className={`${ready ? "stroke-health-600" : "stroke-travel-500"} transition-[stroke-dasharray] duration-700 ease-out motion-reduce:transition-none`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${(circumference * percent) / 100} ${circumference}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-display font-extrabold leading-none text-ink"
          // "100%" is a digit wider; it must not touch the ring.
          style={{ fontSize: Math.round(size / (ready ? 4.4 : 3.6)) }}
        >
          {percent === null ? "—" : `${percent}%`}
        </span>
        {caption && size >= 96 && (
          <span className="mt-1 text-[11px] font-semibold text-muted-foreground">
            {caption}
          </span>
        )}
      </div>
    </div>
  );
};
